import { useContext, useMemo } from 'react';
import styles from '../../styles/Battlefield.module.css';
import BattlefieldScene from '../BattlefieldScene';
import DisplayScene from '../display/DisplayScene';
import { DisplayActionContext } from '../display/displayFaces';
import BattlefieldToolRail from '../BattlefieldToolRail';
import BattlefieldLevelPicker from '../BattlefieldLevelPicker';
import PingWheel from '../PingWheel';
import { curtainHides } from '../../engine/scenes';
import { sceneHandlers } from './sceneGestures';
import { RailsOpenContext } from '../shell/ShellColumns';
import PanelSceneLayers from './PanelSceneLayers';
import PanelBottomLayer from './PanelBottomLayer';
import { useCamera } from '../../hooks/cameraStore';
import { coverOnScreen, screensShown } from './useCoverCue';
import { kickStyleOf } from './panelGeometry';

/**
 * What the stage says to the scene: hidden under a lowered curtain (a player), dimmed beside the badge (the GM,
 * plan map-display I), and when it was last cut or struck, for the display to play.
 */
function stageProps({ stage, isGm }) {
  const curtained = curtainHides(stage, isGm);
  return {
    curtained,
    'data-curtained': curtained ? 'true' : undefined,
    'aria-hidden': curtained || undefined,
    'data-curtain-down': stage?.curtain && isGm ? 'true' : undefined,
    cutAt: stage?.cutAt ?? null,
    struckAt: stage?.struckAt ?? null,
  };
}

/**
 * The scene itself, with every gesture wired in. THE CURTAIN, for a
 * player: the map is there and not shown (v8.339.0) - the SVG stays
 * mounted and is hidden through its own props, so nothing else changes
 * under it. What it hides is what the terrain document already delivered:
 * a courtesy to the table, not a secret (ADR 2026-09-09).
 */
function PanelScene({ panel, map: panelMap }) {
  // In a session, a click on bare ground closes the panels open over the map (Z6c); null in the Assets editors.
  const openRail = useContext(RailsOpenContext);
  // The scene's map: the panel's, with this frame's camera on it. The gestures take the panel's own - they ask
  // `getCamera()` when they happen, never a frame's camera.
  const map = useSceneMap(panelMap);
  const { building, build } = map;
  // The renderer, decided once in useBattlefieldPanel (ADR 2026-10-07).
  const Scene = map.renderer === 'display' ? DisplayScene : BattlefieldScene;
  return (
    <DisplayActionContext.Provider value={map.display}>
      <Scene
        {...stageProps(panel)}
        boundary={map.boundary}
        solids={map.solids}
        camera={map.camera}
        includeHidden={panel.isGm}
        selectedSolidId={building ? build.selected : null}
        voxels={panel.terrain?.voxels || null}
        showTerrainMarks={map.prefs.terrainMarks}
        animation={map.prefs.terrainAnimation}
        {...sceneHandlers({ ...panel, ...panelMap, hoverInfo: map.prefs.hoverInfo, openRail })}
        data-replacing={map.dropOverId ?? undefined}
        data-replace-armed={map.replaceArmed ? 'true' : undefined}
        data-measuring={map.measurePending ? 'true' : undefined}
        data-moving={map.dragOrigin ? 'true' : undefined}
        style={map.kickStyle}
      >
        <PanelSceneLayers panel={panel} map={map} />
      </Scene>
    </DisplayActionContext.Provider>
  );
}

/**
 * THE SCENE'S MAP (perf overhaul 2b, 8f): the panel's map with this frame's camera on it - the camera, the kick's
 * shift for it, and the take-cover cue put on the screen for it. The scene is the one place that subscribes to the
 * camera (hooks/cameraStore): a camera frame re-renders the scene and its layers, never the panel round them.
 */
function useSceneMap(panelMap) {
  const camera = useCamera(panelMap.cameraStore);
  const { cover, kick } = panelMap;
  // Projected only when the cue's own inputs or the camera change: the same per-figure arrays otherwise, so a
  // marker's memo holds across the panel's other renders.
  const { readings, tokens, ghost } = cover ?? {};
  const shown = useMemo(
    () => readings && screensShown(readings, tokens, ghost, camera),
    [readings, tokens, ghost, camera],
  );
  const sceneCover = useMemo(() => shown && coverOnScreen(cover, shown), [cover, shown]);
  const kickStyle = useMemo(() => kickStyleOf(camera, kick), [camera, kick]);
  return useMemo(
    () => ({ ...panelMap, camera, cover: sceneCover ?? cover, kickStyle }),
    [panelMap, camera, sceneCover, cover, kickStyle],
  );
}

/** The sweep, drawn in client coordinates - `position: fixed`, so nothing the map is inside can clip it. */
function SweepBox({ box }) {
  if (!box) return null;
  const style = { left: box.x1, top: box.y1, width: box.x2 - box.x1, height: box.y2 - box.y1 };
  return <div className={styles.sweepBox} data-testid="deploy-sweep" style={style} />;
}

/**
 * The map column: the scene, the ping wheel, the rail beside the map (the
 * tools while building, the storeys otherwise), and the bottom layer.
 *
 * The empty-arena prompt goes ONLY TO SOMEBODY WHO COULD DO SOMETHING
 * ABOUT IT - keyed on whether Build is actually offered, so it follows the
 * toolbar rather than `isGm` (v8.56.0).
 */
export default function PanelMapColumn({ panel, map }) {
  const { building, levels } = map;
  return (
    <div className={styles.stageMap}>
      <PanelScene panel={panel} map={map} />
      <PingWheel wheel={map.pingGesture.wheel} />
      {building && <BattlefieldToolRail build={map.build} />}
      {!building && levels.length > 1 && (
        <BattlefieldLevelPicker
          levels={levels}
          name={map.movingName}
          onPick={map.pickLevel}
          costs={map.levelCosts}
          allowFree={!panel.isLocked('freePlacement')}
        />
      )}
      {map.solids.length === 0 && map.availableModes.includes('build') && (
        <div className={styles.empty}>No battlefield yet. Load a map, or open Build to lay one out.</div>
      )}
      <SweepBox box={map.sweepBoxState} />
      <PanelBottomLayer panel={panel} map={map} />
    </div>
  );
}
